import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CoverLayout from "layouts/authentication/components/CoverLayout";
import api from "../../../utils/axiosConfig";
import bgImage from "assets/images/bg-reset-cover.jpeg";
import IconImage from "assets/images/favicon.png";

const API_RESET_PASSWORD = "/api/auth/reset-password";
const PASSWORD_MIN_LENGTH = 12;

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function ResetPassword() {
  const navigate = useNavigate();
  const query = useQuery();
  // M2 - l'email n'est plus dans l'URL, seulement le token
  const token = query.get("token");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  useEffect(() => {
    if (!token) {
      setSnackbar({
        open: true,
        message: "Lien de réinitialisation invalide ou incomplet.",
        severity: "error",
      });
      setTimeout(() => navigate("/authentication/forgot-password"), 3000);
    }
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== passwordConfirmation) {
      setSnackbar({
        open: true,
        message: "Les mots de passe ne correspondent pas.",
        severity: "error",
      });
      return;
    }
    if (password.length < PASSWORD_MIN_LENGTH) {
      setSnackbar({
        open: true,
        message: `Le mot de passe doit contenir au moins ${PASSWORD_MIN_LENGTH} caractères.`,
        severity: "warning",
      });
      return;
    }
    if (!/[A-Za-z]/.test(password)) {
      setSnackbar({
        open: true,
        message: "Le mot de passe doit contenir au moins une lettre.",
        severity: "warning",
      });
      return;
    }
    if (!/\d/.test(password)) {
      setSnackbar({
        open: true,
        message: "Le mot de passe doit contenir au moins un chiffre.",
        severity: "warning",
      });
      return;
    }
    if (!/[^A-Za-z0-9]/.test(password)) {
      setSnackbar({
        open: true,
        message: "Le mot de passe doit contenir au moins un caractère spécial.",
        severity: "warning",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post(API_RESET_PASSWORD, {
        token,
        password,
        password_confirmation: passwordConfirmation,
      });
      setSnackbar({
        open: true,
        message:
          "Votre mot de passe a été réinitialisé avec succès ! Vous pouvez maintenant vous connecter.",
        severity: "success",
      });
      setTimeout(() => navigate("/authentication/sign-in"), 3000);
    } catch {
      setSnackbar({
        open: true,
        message: "Erreur. Le lien est peut-être expiré ou invalide.",
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!token) {
    return (
      <CoverLayout coverHeight="100vh" image={bgImage}>
        <MDBox pt={3} pb={2} textAlign="center">
          <CircularProgress color="info" />
          <MDTypography variant="body2" color="text" mt={2}>
            Vérification du lien...
          </MDTypography>
        </MDBox>
      </CoverLayout>
    );
  }

  return (
    <CoverLayout image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="purple"
          borderRadius="lg"
          coloredShadow="purple"
          mx={2}
          mt={-3}
          p={3}
          mb={1}
          textAlign="center"
        >
          <MDBox display="flex" alignItems="center" justifyContent="center" sx={{ mb: 1 }}>
            <MDBox component="img" src={IconImage} alt="Logo" width="40px" sx={{ mr: 1.5 }} />
            <MDTypography variant="h5" fontWeight="bold" color="white" sx={{ letterSpacing: 1 }}>
              {process.env.REACT_APP_BRAND_NAME}
            </MDTypography>
          </MDBox>
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={2}>
            Nouveau mot de passe
          </MDTypography>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form" onSubmit={handleSubmit}>
            <MDBox mb={2}>
              <TextField
                type="password"
                label="Nouveau mot de passe"
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                inputProps={{ minLength: PASSWORD_MIN_LENGTH }}
              />
            </MDBox>
            <MDBox mb={4}>
              <TextField
                type="password"
                label="Confirmer le mot de passe"
                fullWidth
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.target.value)}
                required
              />
            </MDBox>
            <MDBox mt={6} mb={1}>
              <MDButton
                variant="gradient"
                color="purple"
                fullWidth
                type="submit"
                disabled={isSubmitting}
                endIcon={isSubmitting && <CircularProgress size={20} color="inherit" />}
              >
                Réinitialiser le mot de passe
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </CoverLayout>
  );
}

export default ResetPassword;
